import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone } from "lucide-react";
import { cld } from "../../lib/cloudinary";
import "./HomeGate.css";

/* ============================================================
   HomeGate — "The Vertical Ledger" + "Tensioned Thread"
   ----------------------------------------------------------
   Full-viewport 50/50 split:
     · Left (employer):  navy + desaturated photo
     · Right (candidate): cream + warm photo
   Hover expands the chosen half to 58%; the unhovered photo
   dims further. A single 1px orange divider runs the middle —
   it bends toward the cursor as if under tension and follows
   the cursor's vertical position. The whole half is the click
   target. No buttons, no cursor swap, no gimmicks.
   ============================================================ */

const DOORS = [
  {
    id: "employer",
    counter: "01",
    edge: "01 / For employers",
    title: "Recruit staff.",
    caption: "Quality Kenyan workforces, ready for you.",
    hint: "Start hiring",
    to: "/employers",
    image: cld("adel/hero-employer", { w: 1920, q: "auto", f: "auto" }),
    tone: "dark",
  },
  {
    id: "candidate",
    counter: "02",
    edge: "02 / For jobseekers",
    title: "Find a job.",
    caption: "We line up the role. No registration fee.",
    hint: "Browse roles",
    to: "/candidates",
    image: cld("adel/hero-candidate", { w: 1920, q: "auto", f: "auto" }),
    tone: "light",
  },
];

/* ───────────────── Tensioned Thread ─────────────────
   A 1px SVG line down the middle of the viewport that bows
   toward the cursor when within proximity. Uses springs so
   movement feels mechanical, not floaty. */
function TensionedThread() {
  const [vp, setVp] = useState({ w: 0, h: 0 });
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    const measure = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    measure();
    window.addEventListener("resize", measure);

    const onMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("mousemove", onMove);
    };
  }, [mouseX, mouseY]);

  /* Deflection ∈ [-12, +12]px, attenuated by distance to centerline */
  const PROXIMITY = 180;
  const MAX_PULL = 12;
  const rawDeflect = useTransform(mouseX, (x) => {
    const cx = vp.w / 2;
    const d = x - cx;
    const absD = Math.abs(d);
    if (absD > PROXIMITY) return 0;
    const strength = 1 - absD / PROXIMITY;
    const dir = d === 0 ? 0 : d / absD;
    return dir * strength * MAX_PULL;
  });
  const deflect = useSpring(rawDeflect, { stiffness: 200, damping: 24, mass: 0.8 });

  /* Path follows cursor Y for the bow's vertex */
  const path = useTransform([deflect, mouseY], ([dx, my]) => {
    const h = vp.h || 1000;
    const yMid = Math.min(Math.max(my, 0), h);
    /* width=30 viewBox, line at x=15, deflection nudges control point */
    return `M 15 0 Q ${15 + dx} ${yMid} 15 ${h}`;
  });

  return (
    <svg
      className="hg2__thread"
      width={30}
      height={vp.h || "100vh"}
      viewBox={`0 0 30 ${vp.h || 1000}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <motion.path
        d={path}
        stroke="rgba(196, 94, 32, 0.85)"
        strokeWidth="1"
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export default function HomeGate() {
  const [hover, setHover] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className={`hg2 ${hover ? `hg2--hover-${hover}` : ""} ${mounted ? "is-mounted" : ""}`}>
      {/* ─── Floating top bar ─── */}
      <header className="hg2__top">
        <Link to="/" className="hg2__brand" aria-label="Adel Group Kenya">
          <span className="hg2__brand-name">Adel Group Kenya</span>
        </Link>
        <a href="tel:+254117222777" className="hg2__phone">
          <Phone size={13} strokeWidth={2.2} />
          +254 117 222 777
        </a>
      </header>

      {/* ─── The two doors ─── */}
      <main className="hg2__doors" role="navigation" aria-label="Choose your path">
        {DOORS.map((d, i) => (
          <Link
            key={d.id}
            to={d.to}
            className={`hg2__door hg2__door--${d.id} hg2__door--${d.tone}`}
            onMouseEnter={() => setHover(d.id)}
            onMouseLeave={() => setHover(null)}
            aria-label={`${d.title} — ${d.hint}`}
          >
            {/* Photo layer */}
            <div className="hg2__photo-wrap" aria-hidden="true">
              <img src={d.image} alt="" className="hg2__photo" />
              <div className="hg2__veil" />
              <div className="hg2__grain" />
            </div>

            {/* Editorial counter watermark (extreme restraint — 8% opacity) */}
            <span className="hg2__num" aria-hidden="true">{d.counter}</span>

            {/* Vertical edge label */}
            <span className="hg2__edge">{d.edge}</span>

            {/* Center content */}
            <div className="hg2__content">
              <motion.h2
                className="hg2__title"
                initial={{ opacity: 0, y: 14 }}
                animate={mounted ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.95, delay: 0.30 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {d.title}
              </motion.h2>

              <motion.p
                className="hg2__caption"
                initial={{ opacity: 0, y: 8 }}
                animate={mounted ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.50 + i * 0.08 }}
              >
                {d.caption}
              </motion.p>

              {/* Hint — fades in only on hover after a beat */}
              <span className="hg2__hint">
                <span>{d.hint}</span>
                <span className="hg2__hint-arrow" aria-hidden="true">→</span>
              </span>
            </div>
          </Link>
        ))}
      </main>

      {/* ─── Signature: tensioned thread divider ─── */}
      <TensionedThread />

      {/* ─── Floating bottom bar ─── */}
      <footer className="hg2__bottom">
        <span>© 2026 Adel Group</span>
        <span className="hg2__bottom-dot" aria-hidden="true">·</span>
        <span>Viewpark Towers, Utaali Lane, Nairobi</span>
      </footer>
    </div>
  );
}
